import { PhoneNumberUtil, PhoneNumberFormat } from "google-libphonenumber";

export function removeNonNumericalCharacters(phone: string): string {
    return phone?.replace(/[^0-9]/g, "") ?? "";
}

export function addCountryCodeToPhone(phone: string): string {
    if (!phone) {
        return null;
    }

    if (phone.startsWith("+")) {
        return phone;
    }

    return phone.startsWith("55") ? `+${phone}` : `+55${phone}`;
}

export function extractDdi(phone: string): string {
    const phoneUtil: PhoneNumberUtil = PhoneNumberUtil.getInstance();
    const number = phoneUtil.parse(phone);

    return number.getCountryCode().toString();
}

export function formatPhone(phone: string): string {
    try {
        let phoneToFormat = "";
        const phoneUtil: PhoneNumberUtil = PhoneNumberUtil.getInstance();
        if (phone.startsWith("+")) {
            return phoneUtil.format(phoneUtil.parse(phone), PhoneNumberFormat.INTERNATIONAL);
        }

        if (phone.startsWith("55")) {
            phoneToFormat = `+${phone}`;
            return phoneUtil.format(phoneUtil.parse(phoneToFormat), PhoneNumberFormat.INTERNATIONAL);
        }

        phoneToFormat = `+55${phone}`;
        return phoneUtil.format(phoneUtil.parse(phoneToFormat), PhoneNumberFormat.INTERNATIONAL);
    } catch (e) {
        return phone;
    }
}

export function detectPhone(message: string): boolean {
    const messageLines = message.split("\n");
    const maybePhones = messageLines.map((line) => {
        if (!line) {
            return;
        }

        const chars = line.split("");
        let currentSegment = null;
        let segments = [];

        /**
         * Separa o texto em segmentos com a seguinte lógica:
         * Escaneia a linha caracter por caracter, ao encontrar um número o armazena em um segmento.
         * Continua armazenando caracteres neste mesmo segmento até encontrar o próximo caracter que é um número.
         * Desses caracteres não númericos mencionados na linha acima, ignora os seguintes: ()- e espaço vazio.
         * Quando encontra o próximo caracter que é número, passa para o próximo segmento e repete o ciclo até o final
         * da linha.
         */
        for (let i = 0; i < chars.length; i++) {
            const char = chars[i];
            const isNumber = Number.isInteger(parseInt(char));
            if (currentSegment === null && !isNumber) {
                continue;
            }

            if (currentSegment === null && isNumber) {
                currentSegment = 0;
                segments[currentSegment] = `${segments[currentSegment] ?? ""}${char}`;
            }

            if (currentSegment !== null && !isNumber && ![" ", "(", ")", "-"].includes(char)) {
                segments[currentSegment] = `${segments[currentSegment] ?? ""}${char}`;
            }

            if (currentSegment !== null && isNumber) {
                currentSegment++;
                segments[currentSegment] = `${segments[currentSegment] ?? ""}${char}`;
            }
        }

        let cleaningSegments = [];
        let currentSegmentIndex = 0;
        /**
         * Nesta parte os segmentos são limpos e concatenados.
         * É verificado se o segmento possui algum caractere de texto junto com os números, caso tenha esse segmento
         * é anulado. Importante, estou assumindo que os textos junto com caracteres são válidos mais isso ainda assim
         * pode ser falho pois o usuário pode escrever o telefone inteiro por extenso ou usando separadores estranhos
         * como . ou , por exemplo.
         * Se o segmento possuir apenas caracteres numéricos, provavelmente é uma sequência. Eles são concatenados em
         * apenas um segmento.
         */
        for (let i = 0; i < segments.length; i++) {
            const segment = segments[i];

            const onlyNumberSegment = segment.replace(/[^0-9]/g, "");
            if (segment.length !== onlyNumberSegment.length) {
                currentSegmentIndex++;
                cleaningSegments[currentSegmentIndex] = "X";
            } else {
                cleaningSegments[currentSegmentIndex] = `${cleaningSegments[currentSegmentIndex] ?? ""}${segment}`;
            }
        }

        cleaningSegments = cleaningSegments.map((seg) => seg.replace(/[^0-9]/g, ""));

        /**
         * Assumo que um número de telefone existe caso, depois de todos os segmentos terem sido removidos de seus
         * caracteres não numéricos. Sobrou alguma string com tamanho entre 8 e 12.
         */
        return cleaningSegments.some((segment) => segment.length >= 8 && segment.length <= 16);
    });

    return maybePhones.some((tests) => tests === true);
}
